/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Card as MuiCard,
  Box,
  Typography as Typo,
  styled,
  TypographyProps,
  Skeleton,
} from '@mui/material';
import NotificationSettingRow from '@jobseeker/lotus/components/NotificationSettingRow';
import {useSelector, useDispatch} from 'react-redux';
import {AppState} from '@jobseeker/common-core/types';
import {saveUserSettings} from '@jobseeker/common-core/reducers/user/thunkServices';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {
  EmailNotificationIcon,
  PushNotificationIcon,
} from '@jobseeker/lotus/assets/icons';
import Image from 'next/image';
import {TrackEventUBA} from '@jobseeker/common-core/utils';
import {useRouter} from 'next/router';

type SettingItem = {
  heading: string;
  description: string;
  proFeature: boolean;
  key: string;
  noPush?: boolean;
};

type SettingGroup = {
  category: string;
  items: SettingItem[];
};

const settingGroups: SettingGroup[] = [
  {
    category: 'JOB ALERTS',
    items: [
      {
        heading: 'New job matches',
        description: 'Daily digest and individual alerts for relevant roles',
        proFeature: false,
        key: 'jobFeed',
      },
      {
        heading: 'Recommended jobs',
        description: 'Jobs that match your profile',
        proFeature: false,
        key: 'boost',
      },
    ],
  },
  {
    category: 'JOB ACTIVITY',
    items: [
      {
        heading: 'Application confirmed',
        description: 'Confirmation when you submit an application',
        proFeature: false,
        key: 'jobApply',
        noPush: true,
      },
      {
        heading: 'Job unpublished',
        description: 'When a job you applied to or saved is taken down',
        proFeature: false,
        key: 'unpublish',
      },
    ],
  },
  {
    category: 'RECRUITER COMMUNICATIONS',
    items: [
      {
        heading: 'Application updates',
        description:
          'Shortlisting and other important updates on your applications',
        proFeature: false,
        key: 'jobAction',
      },
      {
        heading: 'Profile views by recruiters',
        description: 'When your profile is viewed by recruiters',
        proFeature: false,
        key: 'search',
      },
      {
        heading: 'Recruiter messages',
        description: 'New chat messages from recruiters',
        proFeature: false,
        key: 'chat',
      },
      {
        heading: 'Assessments',
        description: 'Receive invites and reminders for recruiter assessments',
        proFeature: false,
        key: 'testPending',
        noPush: true,
      },
    ],
  },
  {
    category: 'GENERAL',
    items: [
      {
        heading: 'Follow-up activity',
        description: 'Details about follow-ups used',
        proFeature: false,
        key: 'followUp',
        noPush: true,
      },
      {
        heading: 'Learning & courses',
        description: 'Course recommendations and career development content',
        proFeature: false,
        key: 'education',
      },
      {
        heading: 'General updates',
        description:
          'Platform announcements, workplace insights, and other relevant updates',
        proFeature: false,
        key: 'promotions',
      },
    ],
  },
];

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));

const Typography = styled(Typo)<TypographyProps>(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const NotificationSettingWidget = (props: {loading: boolean}) => {
  const {loading} = props;
  const dispatch: any = useDispatch();
  const {isPro} = useSelector((state: AppState) => state.auth);
  const isLarge = useIsLargeDevice();
  const router = useRouter();
  const section = Array.isArray(router?.query?.section)
    ? router.query.section[0]
    : router?.query?.section;

  const userSettings = useSelector(
    (state: AppState) => state.user.profile.settings
  );

  const saveSettings = (name: string, value: number, type: 'email' | 'push') => {
    const toggled = value == 1 ? 0 : 1;
    dispatch(
      saveUserSettings(
        type === 'push'
          ? {pnSettings: {[name]: toggled}}
          : {emailSettings: {[name]: toggled}}
      )
    );
    TrackEventUBA('settingsPageClick', 'click', {
      ref: router.query.ref || '',
      pageName: 'Setting Page',
      pageSection: section,
      sessionUsername: `${sessionStorage.getItem('userId')}`,
      clickType: 'notification_toggle_clicked',
      bucket: name,
      channel: type === 'push' ? 'mobile' : 'email',
      from_state: value == 1 ? 'on' : 'off',
      to_state: toggled == 1 ? 'on' : 'off',
    });
  };

  return (
    <Box height="100%" px={{xxs: 1.25, md: 0}}>
      <Card
        sx={{
          boxShadow: isLarge ? 2 : 'none',
          borderRadius: 2,
          marginLeft: {md: 0.625},
          width: '100%',
          // height: {xxs: 'auto', md: '100%'},
        }}
        data-test-id="notification_setting_container"
      >
        <Box
          sx={{
            px: isLarge ? 6 : 0,
            pb: isLarge ? 7 : 0,
            pt: isLarge ? 3 : 0,
            bgcolor: isLarge ? 'transparent' : '#f4f8fb',
          }}
        >
          {isLarge && (
            <Typography
              display="flex"
              fontSize={{xxs: 16, md: 20}}
              fontWeight={400}
              mb={2}
              // mt={2.75}
              sx={{wordWrap: 'break-word', textTransform: 'capitalize'}}
            >
              Notifications
            </Typography>
          )}

          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              bgcolor: '#f4f8fb',
              px: 2,
              py: 1,
              borderTopLeftRadius: '6px',
              borderTopRightRadius: '6px',
              ...(!isLarge && {border: '1px solid #edf2f7'}),
            }}
          >
            <Typography
              fontSize={12}
              fontWeight={500}
              sx={{
                color: '#858585',
                textTransform: 'uppercase',
                letterSpacing: '0.48px',
              }}
            >
              Notification Types
            </Typography>
            <Box display="flex" alignItems="center" gap={{xxs: 3, sm: 10}}>
              <Box
                width={40}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={0.5}
              >
                <Image
                  src={EmailNotificationIcon}
                  width={14}
                  height={14}
                  alt="email notification"
                />
                <Typography
                  fontSize={10}
                  fontWeight={500}
                  sx={{
                    color: '#858585',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    lineHeight: 1,
                  }}
                >
                  Email
                </Typography>
              </Box>
              <Box
                width={40}
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={0.5}
                pt={0.25}
              >
                <Image
                  src={PushNotificationIcon}
                  width={14}
                  height={14}
                  alt="push notification"
                />
                <Typography
                  fontSize={10}
                  fontWeight={500}
                  sx={{
                    color: '#858585',
                    textTransform: 'uppercase',
                    letterSpacing: '0.4px',
                    lineHeight: 1,
                  }}
                >
                  Mobile
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box>
            {settingGroups.map((group, groupIndex) =>
              loading ? (
                <Box
                  key={group.category}
                  sx={{mb: groupIndex < settingGroups.length - 1 ? 2 : 0}}
                >
                  <Box sx={{px: 2, py: 1}}>
                    <Skeleton variant="rounded" width="30%" height={14} />
                  </Box>
                  {group.items.map(item => (
                    <Box
                      key={item.key}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      px={2}
                      py={1.5}
                    >
                      <Box flex={1}>
                        <Skeleton
                          variant="rounded"
                          width="50%"
                          height={16}
                          sx={{mb: 1}}
                        />
                        <Skeleton variant="rounded" width="30%" height={12} />
                      </Box>
                      <Box display="flex" gap={{xxs: 3, sm: 10}} flexShrink={0}>
                        <Skeleton variant="rounded" width={40} height={20} />
                        <Skeleton variant="rounded" width={40} height={20} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box
                  key={group.category}
                  sx={{
                    border: '1px solid #edf2f7',
                    borderRadius: groupIndex === 0 ? '0 0 6px 6px' : '6px',
                    overflow: 'hidden',
                    mb: groupIndex < settingGroups.length - 1 ? 2 : 0,
                  }}
                >
                  <Box sx={{bgcolor: '#f4f8fb', px: 2, py: 1}}>
                    <Typography
                      fontSize={12}
                      fontWeight={500}
                      sx={{
                        color: '#858585',
                        textTransform: 'uppercase',
                        letterSpacing: '0.48px',
                      }}
                    >
                      {group.category}
                    </Typography>
                  </Box>
                  {group.items.map((item, index) => (
                    <NotificationSettingRow
                      key={item.key}
                      heading={item.heading}
                      description={item.description}
                      proFeature={item.proFeature}
                      isPro={isPro}
                      emailValue={!!userSettings?.emailSettings?.[item.key]}
                      pushValue={
                        item.noPush
                          ? false
                          : !!userSettings?.pnSettings?.[item.key]
                      }
                      onEmailChange={() =>
                        saveSettings(
                          item.key,
                          userSettings?.emailSettings?.[item.key],
                          'email'
                        )
                      }
                      onPushChange={() =>
                        saveSettings(
                          item.key,
                          userSettings?.pnSettings?.[item.key],
                          'push'
                        )
                      }
                      noPush={item.noPush}
                      isLastItem={index === group.items.length - 1}
                    />
                  ))}
                </Box>
              )
            )}
          </Box>
        </Box>
      </Card>

    </Box>
  );
};

export default NotificationSettingWidget;
